import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { createRoot } from "react-dom/client";

// ─────────────────────────────────────────────────────────────────────────────
// Shared modal + toast primitives for the Customer Status screens.
//
// Replaces SweetAlert2 (which renders unstyled, off-brand popups) with modals
// that match the rest of the app. Imperative, promise-based API so call sites
// stay terse:
//
//   if (!(await uiConfirm({ title: "Remove?", tone: "danger" }))) return;
//   const note = await uiPrompt({ title: "Note", multiline: true });
//   const { assignee, reason } = await uiPrompt({ title: "…", fields: [...] });
//   uiToast({ type: "error", title: "Couldn't save", message: err });
//
// <UiHost/> self-mounts on import — no provider wiring needed.
// ─────────────────────────────────────────────────────────────────────────────

let uiState = { dialog: null, toasts: [] };
const subs = new Set();
const emit = () => subs.forEach((fn) => fn());
const setState = (patch) => {
  uiState = { ...uiState, ...patch };
  emit();
};

// ── Toasts ─────────────────────────────────────────────────────────────────
let toastSeq = 0;
export const uiToast = ({ type = "info", title, message, duration = 4000 }) => {
  const id = ++toastSeq;
  setState({ toasts: [...uiState.toasts, { id, type, title, message }] });
  if (duration) {
    setTimeout(() => {
      setState({ toasts: uiState.toasts.filter((t) => t.id !== id) });
    }, duration);
  }
  return id;
};
const dismissToast = (id) =>
  setState({ toasts: uiState.toasts.filter((t) => t.id !== id) });

// ── Dialogs ────────────────────────────────────────────────────────────────
let dialogSeq = 0;
const openDialog = (dialog) =>
  new Promise((resolve) => {
    setState({ dialog: { ...dialog, resolve, _id: ++dialogSeq } });
  });

export const uiAlert = ({ type = "info", title, message, confirmText = "OK" }) =>
  openDialog({ kind: "alert", type, title, message, confirmText });

export const uiConfirm = ({
  title,
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  tone = "primary",
}) => openDialog({ kind: "confirm", title, message, confirmText, cancelText, tone });

// Single-field shorthand OR `fields: [...]`. Resolves a string (single) or an
// object keyed by field name (multi), or null on cancel.
export const uiPrompt = ({
  title,
  message,
  confirmText = "Save",
  cancelText = "Cancel",
  tone = "primary",
  fields,
  // single-field shorthand:
  label,
  initial = "",
  placeholder,
  multiline = false,
  select = false,
  options = [],
  required = false,
}) => {
  const resolvedFields = fields || [
    {
      name: "value",
      label,
      type: multiline ? "textarea" : select ? "select" : "text",
      initial,
      placeholder,
      options,
      required,
    },
  ];
  return openDialog({
    kind: "prompt",
    title,
    message,
    confirmText,
    cancelText,
    tone,
    fields: resolvedFields,
    single: !fields,
  });
};

// ── Presentational ─────────────────────────────────────────────────────────
const TONE_BTN = {
  primary: "bg-cyan-600 text-white hover:bg-cyan-500",
  danger: "bg-rose-600 text-white hover:bg-rose-500",
};
const btnBase =
  "rounded-lg px-4 py-2 text-xs font-semibold uppercase tracking-wide transition disabled:opacity-50";
const btnCancel =
  "rounded-lg border border-gray-200 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-600 transition hover:border-gray-300 hover:text-gray-800";

const TOAST_TONE = {
  success: "border-emerald-500",
  error: "border-rose-500",
  warning: "border-amber-500",
  info: "border-cyan-500",
};
const TOAST_ICON = { success: "✓", error: "!", warning: "⚠", info: "i" };

const Lines = ({ message }) => {
  if (message == null || message === "") return null;
  const arr = Array.isArray(message) ? message : [message];
  return (
    <div className="space-y-1">
      {arr.map((m, i) => (
        <p key={i} className="text-sm leading-relaxed text-gray-600">
          {m}
        </p>
      ))}
    </div>
  );
};

const Field = ({ f, value, onChange, autoFocus }) => {
  const cls =
    "w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none transition focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20";
  return (
    <label className="block">
      {f.label && (
        <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-gray-500">
          {f.label}
          {f.required && <span className="text-rose-500"> *</span>}
        </span>
      )}
      {f.type === "textarea" ? (
        <textarea
          autoFocus={autoFocus}
          rows={3}
          className={cls}
          style={{ backgroundColor: "#fff", color: "#111827" }}
          placeholder={f.placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : f.type === "select" ? (
        <select
          autoFocus={autoFocus}
          className={cls}
          style={{ backgroundColor: "#fff", color: "#111827" }}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        >
          {(f.options || []).map((o) => (
            <option
              key={o.value}
              value={o.value}
              style={{ backgroundColor: "#fff", color: "#111827" }}
            >
              {o.label}
            </option>
          ))}
        </select>
      ) : (
        <input
          type={f.type === "date" ? "date" : "text"}
          max={f.type === "date" ? f.max : undefined}
          autoFocus={autoFocus}
          className={cls}
          style={{ backgroundColor: "#fff", color: "#111827" }}
          placeholder={f.placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </label>
  );
};

const Dialog = ({ dialog }) => {
  const { kind, title, message, confirmText, cancelText, tone = "primary" } = dialog;
  const [vals, setVals] = useState(() => {
    const init = {};
    (dialog.fields || []).forEach((f) => {
      init[f.name] = f.initial ?? (f.type === "select" ? f.options?.[0]?.value ?? "" : "");
    });
    return init;
  });
  const closingRef = useRef(false);

  const finish = (result) => {
    if (closingRef.current) return;
    closingRef.current = true;
    const { resolve } = dialog;
    setState({ dialog: null });
    resolve(result);
  };

  const onCancel = () =>
    finish(kind === "confirm" ? false : kind === "prompt" ? null : undefined);

  const onConfirm = () => {
    if (kind === "confirm") return finish(true);
    if (kind === "alert") return finish(undefined);
    // prompt — validate required
    const missing = (dialog.fields || []).find(
      (f) => f.required && !String(vals[f.name] ?? "").trim(),
    );
    if (missing) return;
    finish(dialog.single ? vals.value : vals);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onCancel();
      if (
        e.key === "Enter" &&
        (kind === "confirm" ||
          kind === "alert" ||
          (kind === "prompt" &&
            dialog.fields.length === 1 &&
            dialog.fields[0].type !== "textarea"))
      ) {
        e.preventDefault();
        onConfirm();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vals]);

  return createPortal(
    <div className="fixed inset-0 z-[1300] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]"
        onClick={onCancel}
      />
      <div
        role="dialog"
        aria-modal="true"
        className="relative w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/5"
      >
        <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-5 py-3.5">
          <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
          <button
            onClick={onCancel}
            className="-mr-1 rounded-lg p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
            aria-label="Close"
          >
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
              <path
                d="M5 5l10 10M15 5L5 15"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <div className="space-y-3 px-5 py-4">
          <Lines message={message} />
          {kind === "prompt" &&
            dialog.fields.map((f, i) => (
              <Field
                key={f.name}
                f={f}
                autoFocus={i === 0}
                value={vals[f.name] ?? ""}
                onChange={(v) => setVals((s) => ({ ...s, [f.name]: v }))}
              />
            ))}
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-100 bg-gray-50/60 px-5 py-3">
          {kind !== "alert" && (
            <button className={btnCancel} onClick={onCancel}>
              {cancelText || "Cancel"}
            </button>
          )}
          <button
            autoFocus={kind !== "prompt"}
            className={`${btnBase} ${TONE_BTN[tone] || TONE_BTN.primary}`}
            onClick={onConfirm}
          >
            {confirmText || "OK"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export const UiHost = () => {
  const [, force] = useState(0);
  useEffect(() => {
    const fn = () => force((n) => n + 1);
    subs.add(fn);
    return () => subs.delete(fn);
  }, []);

  return (
    <>
      {uiState.dialog && (
        <Dialog key={uiState.dialog._id} dialog={uiState.dialog} />
      )}
      {uiState.toasts.length > 0 &&
        createPortal(
          <div className="fixed right-4 top-4 z-[1400] flex w-80 max-w-[calc(100vw-2rem)] flex-col gap-2">
            {uiState.toasts.map((t) => (
              <div
                key={t.id}
                onClick={() => dismissToast(t.id)}
                className={`cursor-pointer rounded-xl border-l-4 bg-white px-4 py-3 shadow-lg ring-1 ring-black/5 ${
                  TOAST_TONE[t.type] || TOAST_TONE.info
                }`}
              >
                <div className="flex items-start gap-2">
                  <span
                    className={`mt-0.5 flex h-4 w-4 flex-none items-center justify-center rounded-full text-[10px] font-bold text-white ${
                      t.type === "success"
                        ? "bg-emerald-500"
                        : t.type === "error"
                          ? "bg-rose-500"
                          : t.type === "warning"
                            ? "bg-amber-500"
                            : "bg-cyan-500"
                    }`}
                  >
                    {TOAST_ICON[t.type] || "i"}
                  </span>
                  <div className="min-w-0">
                    {t.title && (
                      <div className="text-sm font-semibold text-gray-900">
                        {t.title}
                      </div>
                    )}
                    {t.message && (
                      <div className="mt-0.5 break-words text-xs text-gray-600">
                        {Array.isArray(t.message) ? t.message.join(" ") : t.message}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
};

// Self-mount once (client only). Safe under HMR via the id guard.
if (typeof document !== "undefined") {
  const HOST_ID = "ncs-ui-host";
  if (!document.getElementById(HOST_ID)) {
    const el = document.createElement("div");
    el.id = HOST_ID;
    document.body.appendChild(el);
    createRoot(el).render(<UiHost />);
  }
}
